import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { message } from '@rendora/common/composables/message';
import { useConfig } from '@rendora/common/composables/useConfig';
import { ApiError } from '@rendora/common/entities';
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import { getOrderCheckPurchaseLimit } from '@rendora/common/entities/order';
import { getRightsIsInCampaign } from '@rendora/common/entities/rights';
import { getI18nService } from '@rendora/common/services/i18n';
import { usePermissionStore } from '@rendora/common/store/permission';
import { useUserStore } from '@rendora/common/store/user';
import { IRightsType, ORDER_TYPE } from '@rendora/common/types/enums';
import { getFeUrl } from '@rendora/common/utils/url';
import { showBuyCreditModal } from './ui/buy-credit/index';
import { showBuyEquityModal } from './ui/buy-equity';
import { showBuyStorageModal } from './ui/buy-storage/index';
import { showUpgradeEquityModal } from './ui/upgrade-equity/index';

export enum Preference {
  PERSONAL = 'personal',
  ENTERPRISE = 'enterprise',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

const DISCOUNT_COUNTDOWN_TIME = 10 * 60; // 10分钟，单位：秒
const COUNTDOWN_INTERVAL = 1000; // 倒计时更新间隔，单位：毫秒

const PERSONAL_TYPES = [IRightsType.PERSONAL, IRightsType.PERSONAL_LITE];
const ENTERPRISE_TYPES = [
  IRightsType.ORG_CUSTOM,
  IRightsType.ORG_FLAGSHIP,
  IRightsType.ORG_STANDARD,
  IRightsType.TEAM_LIGHT,
];
const TEAM_TYPES = [IRightsType.TEAM_LIGHT, IRightsType.ORG_STANDARD, IRightsType.ORG_FLAGSHIP];
const TASTE_TYPES = [IRightsType.TASTE, IRightsType.TEAM_TASTE];

const PRODUCT_IMAGE_MAP: Partial<Record<IRightsType, string>> = {
  [IRightsType.TASTE]: 'personal-experience-edition.png',
  [IRightsType.PERSONAL]: 'personal-edition.png',
  [IRightsType.PERSONAL_LITE]: 'personal-lite-edition.png',
  [IRightsType.TEAM_LIGHT]: 'team-lite-edition.png',
  [IRightsType.TEAM_TASTE]: 'team-experience-edition.png',
  [IRightsType.ORG_CUSTOM]: 'enterprise-edition.png',
  [IRightsType.ORG_STANDARD]: 'enterprise-edition.png',
  [IRightsType.ORG_FLAGSHIP]: 'enterprise-edition.png',
};

export const usePayment = defineStore('payment', () => {
  const userStore = useUserStore();
  const { user, isAdmin, isTeamRight, isPersonLiteOrStandard } = storeToRefs(userStore);
  const { hasActionPermission } = usePermissionStore();
  const { isUS } = useConfig();
  const i18nService = getI18nService();
  const $t = i18nService.t;

  const discountRemainingTime = ref(0);
  const isAcceptedDiscount = ref(false);
  const isBuyEquityModalActive = ref(false);
  const discountTimer = ref<number | null>(null);
  const preference = ref<string>(!isUS ? Preference.PERSONAL : Preference.YEARLY);
  const isDiscount = ref(false);

  const checkUserLevel = (versions: IRightsType[]): boolean => {
    const userRightsType = user.value?.rights_type;
    return userRightsType ? versions.includes(userRightsType) : false;
  };

  const getProductImage = (key: IRightsType): string => {
    const prefix = !isUS
      ? getFeUrl('/youyan-platform/png/products/')
      : getFeUrl('/rendora/products/');

    const imageName = PRODUCT_IMAGE_MAP[key];
    return imageName ? `${prefix}${imageName}` : '';
  };

  const isUpgrade = computed(() => {
    if (!user.value) return false;
    return TASTE_TYPES.includes(user.value.rights_type);
  });

  const isDiscountAvailable = computed(() => {
    return user.value?.rights_type === IRightsType.TASTE;
  });

  const canBuyEquity = computed(() => {
    if (!user.value) return false;

    // 企业版用户不能购买权益包
    if (ENTERPRISE_TYPES.includes(user.value.rights_type)) {
      return false;
    }

    // 团队用户需要权限和管理员身份
    if (isTeamRight.value) {
      return hasActionPermission(GENERIC_ACTION_KEYS.PACKAGE_UPGRADE) && isAdmin.value;
    }

    return true;
  });

  const canBuyStorage = computed(() => {
    // 个人版用户可以直接购买
    if (checkUserLevel(PERSONAL_TYPES)) {
      return true;
    }

    // 团队版用户需要管理员身份
    return checkUserLevel(TEAM_TYPES) && isAdmin.value;
  });

  const canBuyCredit = computed(() => {
    if (user.value?.is_expired) return false;

    // 个人版用户可以直接购买
    if (checkUserLevel(PERSONAL_TYPES)) return true;

    // 团队轻量版用户需要管理员身份
    return checkUserLevel([IRightsType.TEAM_LIGHT]) && isAdmin.value;
  });

  const canAddSubAccount = computed(() => {
    return checkUserLevel([IRightsType.TEAM_LIGHT]) && isAdmin.value && isUS;
  });

  const displayEquityBuyText = computed(() => {
    if (!user.value) return $t('gou-mai-tao-can');
    if (IRightsType.TASTE === user.value?.rights_type) return $t('sheng-ji-quan-yi');
    if (IRightsType.PERSONAL_LITE === user.value?.rights_type) return $t('sheng-ji-quan-yi');
    if (IRightsType.TEAM_TASTE === user.value?.rights_type) return $t('sheng-ji-quan-yi');
    return $t('xu-fei-quan-yi');
  });

  const handleBuyEquity = (targetType?: IRightsType) => {
    showBuyEquityModal({ targetType });
  };

  const handleUpgradeEquity = () => {
    showUpgradeEquityModal();
  };

  const handleBuyStorage = () => {
    showBuyStorageModal();
  };

  const handleBuyCredit = () => {
    showBuyCreditModal();
  };

  const checkPurchaseLimit = async (order_type: ORDER_TYPE): Promise<void> => {
    const res = await getOrderCheckPurchaseLimit({ order_type });

    if (res.error_code !== 0 && res.error_reason) {
      message.warning(res.error_reason);
      throw new ApiError(res.error_reason);
    }
  };

  const startDiscountCountdown = (): void => {
    if (discountTimer.value) {
      clearInterval(discountTimer.value);
    }

    discountRemainingTime.value = DISCOUNT_COUNTDOWN_TIME;

    discountTimer.value = window.setInterval(() => {
      if (discountRemainingTime.value <= 0) {
        stopDiscountCountdown();
        return;
      }
      discountRemainingTime.value -= 1;
    }, COUNTDOWN_INTERVAL);
  };

  const stopDiscountCountdown = (): void => {
    if (discountTimer.value) {
      clearInterval(discountTimer.value);
      discountTimer.value = null;
    }
    discountRemainingTime.value = 0;
    isAcceptedDiscount.value = false;
  };

  const updateDiscountAccepted = (accepted: boolean): void => {
    isAcceptedDiscount.value = accepted;
  };

  const updateBuyEquityModalActive = (active: boolean): void => {
    isBuyEquityModalActive.value = active;
  };

  const showDiscountTip = computed(() => {
    // 订阅新套餐 连续订阅入门/标准
    return isPersonLiteOrStandard.value && user.value?.already_used_rights.is_subscription;
  });

  const fetchDiscount = async () => {
    try {
      const res = await getRightsIsInCampaign();
      /**
       * 订阅新套餐： 入门，标准版 88
       * 订阅新套餐：连续订阅入门，标准 88  付费墙页面说明
       * 已有套餐续费： 入门，标准 88
       * 已有套餐续费： 连续订阅入门，标准 88
       */
      isDiscount.value = res.data.flag;
    } catch (err) {
      console.error(err);
    }
  };

  return {
    preference,
    discountRemainingTime,
    isAcceptedDiscount,
    isUpgrade,
    isDiscountAvailable,
    isDiscount,
    isBuyEquityModalActive,
    displayEquityBuyText,
    canBuyEquity,
    canBuyStorage,
    canBuyCredit,
    canAddSubAccount,
    showDiscountTip,
    handleBuyEquity,
    handleBuyStorage,
    handleBuyCredit,
    handleUpgradeEquity,
    checkPurchaseLimit,
    getProductImage,
    startDiscountCountdown,
    stopDiscountCountdown,
    updateDiscountAccepted,
    updateBuyEquityModalActive,
    fetchDiscount,
  };
});
